<script setup lang="ts">
/**
 * The plain-words preview a form shows before it saves (#1756, FM8). It opens in place, under the form, and says what
 * pressing the button will do; nothing is sent until the founder confirms. The confirm button carries the form's own
 * primary label, and Edit goes back to the form with every value kept. Each button emits its own event and neither
 * submits the form it sits in (`type="button"`).
 *
 * Focus moves into the preview when it opens, so a keyboard or screen-reader user lands on what they are asked to
 * confirm rather than on the button that opened it.
 */
import { onMounted, ref } from 'vue'
import { Button } from './ui/button'
import ZhPanel from './ZhPanel.vue'

defineProps<{
    lines: string[]
    confirmLabel: string
    /** The save is under way: confirm is held so it cannot be sent twice. */
    pending?: boolean
    /**
     * The preview draws no surface of its own: its lines and buttons sit in a plain `div`, not a panel. For a form
     * that already sits on a surface and is itself `bare` (the Agents connection editor's panel), so opening the
     * preview never nests a panel inside that one. Everything else is unchanged.
     */
    bare?: boolean
}>()

const emit = defineEmits<{
    confirm: []
    edit: []
}>()

const root = ref<HTMLElement | null>(null)

onMounted(() => {
    root.value?.focus()
})
</script>

<template>
    <div ref="root" data-slot="save-preview" tabindex="-1" role="region" aria-label="Before you save" class="outline-none">
        <component :is="bare ? 'div' : ZhPanel" v-bind="bare ? {} : { measure: 'prose' }" class="space-y-3">
            <p class="text-muted-foreground font-mono text-xs uppercase">Before you save{{ ' ' }}</p>
            <!-- Each line ends on a space, so the preview reads (and is copied) as sentences, never "run.Edit". -->
            <p v-for="(line, index) in lines" :key="index" class="text-foreground text-sm">{{ `${line} ` }}</p>
            <div class="flex flex-wrap items-center justify-end gap-2">
                <Button type="button" size="sm" variant="outline" @click="emit('edit')">Edit</Button>
                <Button type="button" size="sm" :disabled="pending" @click="emit('confirm')">{{ confirmLabel }}</Button>
            </div>
        </component>
    </div>
</template>
